import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Seo } from '@/lib/seo';
import { Loader } from '@/components/ui/Icons';
import { quoteServiceOptions } from '@/data/services';

type Status = 'new' | 'contacted' | 'quoted' | 'won' | 'lost';
const STATUSES: Status[] = ['new', 'contacted', 'quoted', 'won', 'lost'];

type Quote = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  service: string;
  status: Status;
  message: string;
  created_at: string;
};

type ListResponse = {
  ok: true;
  quotes: Quote[];
  page: number;
  per_page: number;
  total: number;
};

export default function AdminQuotes() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<Status | ''>('');
  const [service, setService] = useState('');
  const [q, setQ] = useState('');

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('per_page', '20');
    if (status) p.set('status', status);
    if (service) p.set('service', service);
    if (q) p.set('q', q);
    return p.toString();
  }, [page, status, service, q]);

  const { data, isLoading, isError, refetch } = useQuery<ListResponse>({
    queryKey: ['admin-quotes', params],
    queryFn: () => api<ListResponse>(`/admin/quotes?${params}`),
  });

  return (
    <>
      <Seo title="Quotes" description="Quote inbox" path="/admin/quotes" noindex />

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-700">Inbox</p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-navy-900">Quote requests</h1>
          <p className="mt-1 text-sm text-ink-700">
            All quote enquiries from the public site land here.
          </p>
        </div>
        {data && (
          <span className="rounded-full bg-navy-900 px-3 py-1 text-xs font-semibold text-gold-300">
            {data.total} total
          </span>
        )}
      </header>

      <div className="mt-6 grid gap-3 rounded-lg border border-paper-100 bg-paper-0 p-4 shadow-card sm:grid-cols-2 lg:grid-cols-4">
        <input
          type="search"
          placeholder="Search name or email"
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          className="rounded-md border border-paper-100 px-3 py-2 text-sm"
        />
        <select
          value={status}
          onChange={(e) => { setPage(1); setStatus(e.target.value as Status | ''); }}
          className="rounded-md border border-paper-100 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={service}
          onChange={(e) => { setPage(1); setService(e.target.value); }}
          className="rounded-md border border-paper-100 px-3 py-2 text-sm"
        >
          <option value="">All services</option>
          {quoteServiceOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button onClick={() => void refetch()} className="btn-ghost text-sm">Refresh</button>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-paper-100 bg-paper-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-ink-700">
            <Loader className="mr-2 h-5 w-5" /> Loading…
          </div>
        ) : isError ? (
          <div className="p-6 text-red-700">Failed to load quotes.</div>
        ) : (
          <table className="min-w-full divide-y divide-paper-100 text-sm">
            <thead className="bg-paper-50 text-left text-xs uppercase tracking-wide text-ink-700">
              <tr>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-paper-100">
              {data?.quotes.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-ink-700">No quotes match these filters.</td></tr>
              )}
              {data?.quotes.map((qrow) => (
                <tr key={qrow.id} className="hover:bg-paper-50">
                  <td className="whitespace-nowrap px-4 py-3 text-ink-700">
                    {new Date(qrow.created_at).toLocaleString('en-GB')}
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/quotes/${qrow.id}`} className="font-semibold text-navy-800 hover:text-gold-700">
                      {qrow.name}
                    </Link>
                    <div className="text-xs text-ink-700">{qrow.email}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-700">{qrow.service}</td>
                  <td className="px-4 py-3"><StatusPill status={qrow.status} /></td>
                  <td className="max-w-md truncate px-4 py-3 text-ink-700">{qrow.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > data.per_page && (
        <div className="mt-4 flex items-center justify-between text-sm text-ink-700">
          <span>
            Page {data.page} · {data.total} total
          </span>
          <div className="flex gap-2">
            <button
              className="btn-ghost"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page <= 1}
            >Previous</button>
            <button
              className="btn-ghost"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.page * data.per_page >= data.total}
            >Next</button>
          </div>
        </div>
      )}
    </>
  );
}

function StatusPill({ status }: { status: Status }) {
  const map: Record<Status, string> = {
    new: 'bg-gold-100 text-gold-700',
    contacted: 'bg-blue-100 text-blue-800',
    quoted: 'bg-purple-100 text-purple-800',
    won: 'bg-green-100 text-green-800',
    lost: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[status]}`}>
      {status}
    </span>
  );
}
